import {
  automaticThoughtIdType,
  ConceptKeyType_automaticThought,
} from "api/conceptApi";
import {
  DimmedLabelLine,
  GuideTypographyLine,
  Label,
  LineWithSolidLabel,
  Percentage,
  PieChart,
  Select,
  SolidLabelLine,
  TextareaLine,
  TypographyFromDashboardLine,
  TypographyFromData,
  TypographyFromDataLine,
  TypographyLine,
} from "data/CellComponent";
import {
  Header1,
  Instruction,
  InstructionWithContent,
  InstructionWithData,
  InstructionWithDataWithContent,
  InstructionWithDataWithTextarea,
  InstructionWithLabelAndPreset,
} from "../../BlockComponent";
import { Content_4_5_A_end, Content_4_5_A_start } from "./4-5-A";
import { v4 as uuidv4 } from "uuid";

export const Content_4_5_1_A = (automaticThoughtId: automaticThoughtIdType) => [
  ...Content_4_5_A_start(automaticThoughtId),

  Header1(`typography_0_0_0`),

  InstructionWithContent(`typography_1_0_0`, [
    ...LineWithSolidLabel(
      "상황",
      TypographyFromDashboardLine(
        `automaticThought${automaticThoughtId}` as ConceptKeyType_automaticThought,
        "situation",
        { id: "상황" }
      )
    ),
  ]),
  InstructionWithContent(`typography_2_0_0`, [
    ...LineWithSolidLabel(
      "자동적 사고",
      TypographyFromDashboardLine(
        `automaticThought${automaticThoughtId}` as ConceptKeyType_automaticThought,
        "thought",
        { id: "생각" }
      )
    ),
  ]),

  InstructionWithLabelAndPreset(`typographyFromData_3_0_0_0`, "사건", {
    id: "사건",
    placeholder: "내담자가 겪은 과거의 일을 적어주세요.",
    presetKey: "event",
  }),

  InstructionWithLabelAndPreset(`typographyFromData_3_0_0`, "나의 요인", {
    id: "나의 요인",
    placeholder: "내담자가 생각하는 자신의 기여를 적어주세요",
    presetKey: "myContribution",
  }),

  InstructionWithData({ A: { id: `상황` } }, `typographyFromData_4_0_0`),
  InstructionWithContent(`typography_5_0_0`, [
    ...LineWithSolidLabel("상황", TypographyLine(`typography_5_2_0`)),
  ]),
  InstructionWithContent(`typography_6_0_0`, [
    ...LineWithSolidLabel("자동적 사고", TypographyLine(`typography_6_2_0`)),
  ]),
  InstructionWithContent(`typography_6_3_0`, [
    ...LineWithSolidLabel("사건", TypographyLine(`typography_6_4_0`)),
  ]),
  InstructionWithContent(`typography_7_0_0`, [
    ...LineWithSolidLabel("요인", TypographyLine(`typography_7_2_0`)),
  ]),
  Instruction(`typography_8_0_0`),
  Instruction(`typography_9_0_0`),
  Instruction(`typography_10_0_0`),
  Instruction(`typography_11_0_0`),
  Instruction(`typography_12_0_0`),
  Instruction(`typography_13_0_0`),

  InstructionWithData({ A: { id: "사건" } }, `typography_14_0_0`),
  Instruction(`typography_15_0_0`),
  Instruction(`typography_16_0_0`),
  Instruction(`typography_16_0_1`),

  InstructionWithDataWithContent(
    { A: { id: "사건" } },
    `typographyFromData_17_0_0`,
    [
      SolidLabelLine("나의 요인"),
      TypographyFromDataLine({ A: { id: "나의 요인" } }, "$A$"),
      ...Array.from({ length: 5 }, (v, i) => i + 1).flatMap((each) => [
        SolidLabelLine(`요인`, { labelIndex: each }),
        TextareaLine({ id: `요인 ${each}` }),
      ]),
    ]
  ),

  Header1(`typography_18_0_0`),
  Instruction(`typography_19_0_0`),
  InstructionWithDataWithContent(
    { A: { id: `사건` } },
    `typographyFromData_20_0_0`,
    [
      ...Array.from({ length: 6 }, (v, i) => i + 1).flatMap((each) => [
        SolidLabelLine(each === 6 ? "나의 요인" : `요인`, {
          requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
          ...(each < 6 && { labelIndex: each }),
        }),
        TypographyFromDataLine(
          { A: { id: each === 6 ? "나의 요인" : `요인 ${each}` } },
          "$A$",
          { requiredId: each === 6 ? "나의 요인" : `요인 ${each}` }
        ),
        DimmedLabelLine("기여도", {
          requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
        }),
        [
          Percentage({
            id: each === 6 ? "나의 요인 확률" : `요인 ${each} 확률`,
            placeholder: "0: 기여가 전혀 없음, 100: 전적으로 기여함",
            warnIfZero:
              each === 6
                ? "기여도는 0%보다 커야 합니다. 위의 다른 요인들의 기여도를 수정하여 현실적인 기여도로 조정해주세요."
                : "기여도는 0%보다 커야 합니다. 정말 0%라 생각한다면 영향을 미쳤을 법한 내용으로 수정 후 진행해주세요.",
            requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
            ...(each === 6 && {
              complementPercentageIdList: Array.from(
                { length: 5 },
                (v, i) => i + 1
              ).map((each) => `요인 ${each} 확률`),
            }),
          }),
        ],
      ]),

      TypographyFromDataLine(
        {
          A: { id: "요인 1 확률" },
          B: { id: "요인 2 확률" },
          C: { id: "요인 3 확률" },
          D: { id: "요인 4 확률" },
          E: { id: "요인 5 확률" },
          F: { id: "나의 요인 확률" },
        },
        "typographyFromData_21_0_0",
        {
          color: "danger.solidBg",
          level: "body-sm",
          percentageIdList: Array.from({ length: 6 }, (v, i) => i + 1).map(
            (each) => (each === 6 ? "나의 요인 확률" : `요인 ${each} 확률`)
          ),
        }
      ),
    ]
  ),
  Instruction(`typography_21_0_0`),
  Instruction(`typography_22_0_0`),
  Instruction(`typography_23_0_0`),
  InstructionWithContent(`typography_24_0_0`, [
    ...Array.from({ length: 6 }, (v, i) => i + 1).flatMap((each) => [
      SolidLabelLine(each === 6 ? "나의 요인" : `요인`, {
        requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
        ...(each < 6 && { labelIndex: each }),
      }),
      TypographyFromDataLine(
        { A: { id: each === 6 ? "나의 요인" : `요인 ${each}` } },
        "$A$",
        {
          pieChartDataId: `chart2_label_${each === 6 ? 0 : each}`,
          requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
        }
      ),
      [
        Label("기여도", "dimmed", {
          width: 0.4,
          requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
        }),
        Label("통제 가능 여부", "dimmed", {
          requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
        }),
      ],

      [
        TypographyFromData(
          {
            A: {
              id: each === 6 ? "나의 요인 확률" : `요인 ${each} 확률`,
            },
          },
          "$A$",
          {
            width: 0.4,
            pieChartDataId: `chart1_value_${each}/chart2_value_${
              each === 6 ? 0 : each
            }`,
            requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
          }
        ),
        Select(
          [
            { translationKey: "통제 불가능" },
            {
              translationKey: "통제 가능",
            },
          ],
          {
            pieChartDataId: `chart1_label_${each}`,
            requiredId: each === 6 ? "나의 요인" : `요인 ${each}`,
            editorKey: uuidv4(),
          }
        ),
      ],
    ]),
  ]),

  Instruction(`typography_25_0_0`),

  Header1(`typography_26_0_0`),
  InstructionWithContent(`typography_27_0_0`, [PieChart("chart1")]),

  InstructionWithData(
    { A: { id: "사건" }, B: { id: "chart1_통제 불가능" } },
    "typographyFromData_31_0_0"
  ),

  InstructionWithContent(`typography_32_0_0`, [PieChart("chart2")]),

  InstructionWithData(
    { A: { id: "사건" }, B: { id: "나의 요인 확률" } },
    "typographyFromData_36_0_0"
  ),

  InstructionWithDataWithTextarea(
    { A: { id: "나의 요인" } },
    "typographyFromData_37_0_0"
  ),

  ...Content_4_5_A_end(automaticThoughtId, 1),
];
